/**
 * Push Notification Service
 * Handles service worker registration and push subscription management
 */

import { apiClient } from '@/api/client'

const SW_PATH = '/sw.js'

interface VapidKeyResponse {
  publicKey: string | null
  configured: boolean
}

interface SubscribeResponse {
  success: boolean
  id: string
  message: string
}

/**
 * Check if push notifications are supported
 */
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/**
 * Get current notification permission status
 */
export function getPermissionStatus(): NotificationPermission {
  if (!isPushSupported()) return 'denied'
  return Notification.permission
}

/**
 * Request notification permission
 */
export async function requestPermission(): Promise<NotificationPermission> {
  if (!isPushSupported()) {
    console.warn('[Push] Push notifications not supported')
    return 'denied'
  }

  const permission = await Notification.requestPermission()
  console.log('[Push] Permission status:', permission)
  return permission
}

/**
 * Register service worker
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[Push] Service workers not supported')
    return null
  }

  try {
    const registration = await navigator.serviceWorker.register(SW_PATH, {
      scope: '/',
    })
    console.log('[Push] Service worker registered:', registration.scope)
    return registration
  } catch (error) {
    console.error('[Push] Service worker registration failed:', error)
    return null
  }
}

/**
 * Get VAPID public key from server
 */
async function getVapidPublicKey(): Promise<string | null> {
  try {
    const response = await apiClient.get<VapidKeyResponse>('/api/v1/me/push-subscription/vapid-key')
    if (!response.configured || !response.publicKey) {
      console.warn('[Push] VAPID not configured on server')
      return null
    }
    return response.publicKey
  } catch (error) {
    console.error('[Push] Failed to get VAPID key:', error)
    return null
  }
}

/**
 * Convert VAPID key from base64 to Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPush(): Promise<boolean> {
  if (!isPushSupported()) {
    console.warn('[Push] Push notifications not supported')
    return false
  }

  // Request permission first
  const permission = await requestPermission()
  if (permission !== 'granted') {
    console.warn('[Push] Notification permission denied')
    return false
  }

  // Get service worker registration
  const registration = await navigator.serviceWorker.ready
  if (!registration) {
    console.error('[Push] No service worker registration')
    return false
  }

  // Check for existing subscription
  let subscription = await registration.pushManager.getSubscription()

  if (!subscription) {
    // Get VAPID public key
    const vapidPublicKey = await getVapidPublicKey()
    if (!vapidPublicKey) {
      console.error('[Push] VAPID key not available')
      return false
    }

    // Subscribe
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
      })
      console.log('[Push] Created new push subscription')
    } catch (error) {
      console.error('[Push] Failed to subscribe:', error)
      return false
    }
  } else {
    console.log('[Push] Using existing push subscription')
  }

  // Send subscription to server
  try {
    await apiClient.post<SubscribeResponse>('/api/v1/me/push-subscription', {
      subscription: subscription.toJSON(),
    })
    console.log('[Push] Subscription saved to server')
    return true
  } catch (error) {
    console.error('[Push] Failed to save subscription:', error)
    return false
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!isPushSupported()) return true

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()

    if (subscription) {
      // Unsubscribe from push manager
      await subscription.unsubscribe()

      // Remove from server
      await apiClient.delete('/api/v1/me/push-subscription', {
        data: { endpoint: subscription.endpoint },
      })

      console.log('[Push] Unsubscribed from push notifications')
    }

    return true
  } catch (error) {
    console.error('[Push] Failed to unsubscribe:', error)
    return false
  }
}

/**
 * Check if currently subscribed to push
 */
export async function isSubscribedToPush(): Promise<boolean> {
  if (!isPushSupported()) return false

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    return subscription !== null
  } catch {
    return false
  }
}

/**
 * Initialize push notifications
 * Call this on app startup
 */
export async function initializePushNotifications(): Promise<void> {
  if (!isPushSupported()) {
    console.log('[Push] Push notifications not supported on this browser')
    return
  }

  // Register service worker
  const registration = await registerServiceWorker()
  if (!registration) {
    console.warn('[Push] Service worker not registered')
    return
  }

  // Check if already subscribed
  const isSubscribed = await isSubscribedToPush()
  if (isSubscribed) {
    console.log('[Push] Already subscribed to push notifications')
  }
}
